import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
    ArrowRight, ShieldCheck, Star, Sparkles,
    Share2, Instagram, Linkedin, Trophy,
    CheckCircle2, Lock, Zap,
} from 'lucide-react';

/**
 * SoloLandingPage — Landing dédiée aux agents commerciaux indépendants.
 *
 * Objectif : convertir l'agent solo (mandataire, indépendant, hors
 * agence B2B) vers /register/solo. Ton "Strava-like" : outil personnel,
 * puissant, gratuit à essayer, réservé aux professionnels vérifiés.
 *
 * DA : identique à la landing principale (fond #050308, gradient
 * violet → orange, wordmark Homly).
 */
export default function SoloLandingPage() {
    return (
        <div className="min-h-screen bg-[#050308] text-white overflow-x-hidden" data-testid="solo-landing-page">
            {/* Halos ambiants Homly */}
            <div className="pointer-events-none fixed inset-0 overflow-hidden -z-0">
                <div className="absolute -top-40 -left-40 w-[700px] h-[700px] bg-[#681793]/20 blur-[140px] rounded-full" />
                <div className="absolute top-1/2 -right-40 w-[600px] h-[600px] bg-[#FF914D]/15 blur-[140px] rounded-full" />
                <div className="absolute -bottom-40 left-1/2 w-[500px] h-[500px] bg-[#A855F7]/12 blur-[130px] rounded-full" />
            </div>

            {/* ── Nav minimale ────────────────────────────────── */}
            <nav className="relative z-10 flex items-center justify-between px-6 sm:px-10 py-6" data-testid="solo-nav">
                <Link to="/" className="text-2xl font-extrabold tracking-tight bg-gradient-to-r from-[#A855F7] to-[#FF914D] bg-clip-text text-transparent">
                    Homly
                </Link>
                <div className="flex items-center gap-3">
                    <Link to="/login" className="text-sm font-semibold text-[#C7BEE6] hover:text-white transition-colors">
                        Se connecter
                    </Link>
                    <Link
                        to="/register/solo"
                        data-testid="solo-landing-nav-cta"
                        className="px-4 py-2 rounded-full bg-gradient-to-r from-[#681793] to-[#FF914D] text-sm font-bold text-white shadow-lg shadow-[#681793]/30 hover:opacity-95 transition-opacity"
                    >
                        Créer mon compte
                    </Link>
                </div>
            </nav>

            {/* ── Hero ─────────────────────────────────────────── */}
            <section className="relative z-10 px-6 sm:px-10 pt-10 sm:pt-16 pb-24 max-w-6xl mx-auto">
                <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="max-w-3xl"
                >
                    <div className="inline-flex items-center gap-2 mb-6 px-4 py-2 rounded-full border border-[#A855F7]/30 bg-[#A855F7]/5 text-xs font-bold tracking-[3px] text-[#FF914D]">
                        <Sparkles className="w-3.5 h-3.5" />
                        RÉSERVÉ AUX AGENTS INDÉPENDANTS
                    </div>
                    <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] mb-6">
                        Votre réputation d&apos;agent,{' '}
                        <span className="bg-gradient-to-r from-[#A855F7] to-[#FF914D] bg-clip-text text-transparent">
                            partagée sur tous vos réseaux.
                        </span>
                    </h1>
                    <p className="text-lg sm:text-xl text-[#C7BEE6] leading-relaxed mb-8 max-w-2xl">
                        Homly est la plateforme d&apos;avis vérifiés pour agents commerciaux indépendants.
                        Collectez, exposez et diffusez votre confiance en 3 clics — comme Strava
                        pour vos avis clients.
                    </p>
                    <div className="flex flex-wrap items-center gap-3 mb-4">
                        <Link
                            to="/register/solo"
                            data-testid="solo-landing-hero-cta"
                            className="group inline-flex items-center gap-2 px-6 py-4 rounded-full bg-gradient-to-r from-[#681793] to-[#FF914D] text-base font-bold text-white shadow-xl shadow-[#681793]/40 hover:opacity-95 transition-all hover:scale-[1.02]"
                        >
                            Créer mon compte solo (30 secondes)
                            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </Link>
                        <Link
                            to="/login"
                            className="inline-flex items-center gap-2 px-6 py-4 rounded-full border border-white/10 bg-white/5 text-sm font-semibold text-[#C7BEE6] hover:border-[#A855F7]/40 hover:text-white transition-colors"
                        >
                            J&apos;ai déjà un compte
                        </Link>
                    </div>
                    <p className="text-xs text-[#8A7DB8] flex items-center gap-2">
                        <ShieldCheck className="w-3.5 h-3.5 text-[#FF914D]" />
                        Vérification RSAC obligatoire · Aucune carte bancaire requise
                    </p>
                </motion.div>
            </section>

            {/* ── Bénéfices (3 piliers) ────────────────────────── */}
            <section className="relative z-10 px-6 sm:px-10 pb-24 max-w-6xl mx-auto" data-testid="solo-benefits">
                <div className="text-center mb-12">
                    <div className="text-xs font-bold tracking-[3px] text-[#FF914D] mb-3">CE QUE VOUS OBTENEZ</div>
                    <h2 className="text-3xl sm:text-4xl font-extrabold">
                        Trois leviers pour <span className="bg-gradient-to-r from-[#A855F7] to-[#FF914D] bg-clip-text text-transparent">devenir la référence locale</span>
                    </h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                    <BenefitCard
                        icon={<Star className="w-6 h-6" />}
                        title="Avis 100% vérifiés"
                        text="Chaque avis est authentifié par le mandat signé. Fini les faux avis Google — vos clients savent que c'est vrai."
                        accent="from-[#A855F7] to-[#FF914D]"
                    />
                    <BenefitCard
                        icon={<Share2 className="w-6 h-6" />}
                        title="Studio de partage"
                        text="4 visuels prêts pour Instagram, LinkedIn et Facebook. QR intégré, format optimal, téléchargement PNG en 1 clic."
                        accent="from-[#681793] to-[#A855F7]"
                    />
                    <BenefitCard
                        icon={<Trophy className="w-6 h-6" />}
                        title="Badge Master doré"
                        text="À 20 avis vérifiés et 4.5⭐+, votre badge public passe en Master doré — bord doré, couronne, tag exclusif."
                        accent="from-[#FF914D] to-[#A855F7]"
                    />
                </div>
            </section>

            {/* ── Comment ça marche (3 étapes) ─────────────────── */}
            <section className="relative z-10 px-6 sm:px-10 pb-24 max-w-6xl mx-auto" data-testid="solo-steps">
                <div className="text-center mb-12">
                    <div className="text-xs font-bold tracking-[3px] text-[#FF914D] mb-3">3 ÉTAPES · 15 MINUTES DE SETUP</div>
                    <h2 className="text-3xl sm:text-4xl font-extrabold">
                        De votre inscription à votre premier partage viral
                    </h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                    <StepCard
                        number="01"
                        title="Vérification RSAC"
                        text="Renseignez votre numéro d'inscription au Registre spécial des Agents commerciaux. Validation instantanée par algorithme officiel."
                    />
                    <StepCard
                        number="02"
                        title="Contactez 5 clients"
                        text="Envoyez le lien de votre profil Homly à vos 5 derniers vendeurs satisfaits. Ils déposent un avis en 2 minutes."
                    />
                    <StepCard
                        number="03"
                        title="Publiez le visuel"
                        text="Ouvrez le Studio, téléchargez le visuel Instagram Story et publiez-le. Chaque scan de QR est tracé — vous voyez votre viralité en temps réel."
                    />
                </div>
            </section>

            {/* ── Pourquoi RSAC ────────────────────────────────── */}
            <section className="relative z-10 px-6 sm:px-10 pb-24 max-w-4xl mx-auto" data-testid="solo-rsac-block">
                <div className="rounded-3xl border border-[#A855F7]/20 bg-gradient-to-br from-[#1A0F2E]/60 via-[#0B0913]/60 to-[#050308] backdrop-blur-xl p-8 sm:p-10 relative overflow-hidden">
                    <div className="pointer-events-none absolute -top-20 -right-20 w-64 h-64 bg-[#FF914D]/15 blur-[80px] rounded-full" />
                    <div className="relative">
                        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#681793] to-[#FF914D] flex items-center justify-center mb-5">
                            <ShieldCheck className="w-7 h-7 text-white" />
                        </div>
                        <h3 className="text-2xl sm:text-3xl font-extrabold mb-3">
                            Pourquoi votre numéro RSAC ?
                        </h3>
                        <p className="text-[#C7BEE6] text-base leading-relaxed mb-5">
                            Contrairement à toutes les plateformes d&apos;avis grand public, Homly
                            n&apos;accepte que des professionnels <strong className="text-white">légalement autorisés à
                            exercer</strong>. Votre numéro d&apos;inscription au Registre Spécial des Agents
                            Commerciaux (RSAC) est vérifié en temps réel par l&apos;algorithme officiel
                            de l&apos;INSEE (checksum Luhn).
                        </p>
                        <p className="text-[#C7BEE6] text-base leading-relaxed">
                            Résultat : quand un client voit un badge Homly, il a la garantie
                            absolue que l&apos;agent en face de lui est <strong className="text-white">vrai</strong>.
                            C&apos;est ce qui rend nos avis 10× plus crédibles.
                        </p>
                    </div>
                </div>
            </section>

            {/* ── Ce qui est verrouillé (transparence) ─────────── */}
            <section className="relative z-10 px-6 sm:px-10 pb-24 max-w-4xl mx-auto" data-testid="solo-limits">
                <div className="rounded-3xl border border-white/10 bg-white/[0.02] p-8">
                    <div className="flex items-start gap-4">
                        <div className="shrink-0 w-11 h-11 rounded-xl bg-[#FF914D]/15 flex items-center justify-center">
                            <Lock className="w-5 h-5 text-[#FF914D]" />
                        </div>
                        <div>
                            <h3 className="text-xl font-bold mb-2">Sections réservées aux agences abonnées</h3>
                            <p className="text-sm text-[#C7BEE6] leading-relaxed mb-4">
                                Le mode Solo vous ouvre 100% des fonctionnalités <strong className="text-white">côté réputation
                                et diffusion</strong>. Les sections Pilotage (KPIs équipe, objectifs collectifs) et Équipe
                                (coaching, certifications inter-agences) restent verrouillées 🔒 tant que votre
                                agence n&apos;a pas rejoint Homly.
                            </p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                                <LimitItem ok>Studio de Partage complet</LimitItem>
                                <LimitItem ok>Badge public + QR viral</LimitItem>
                                <LimitItem ok>Avis vérifiés illimités</LimitItem>
                                <LimitItem ok>Compteur de vues du badge</LimitItem>
                                <LimitItem>Pilotage KPIs équipe</LimitItem>
                                <LimitItem>Coaching inter-agences</LimitItem>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── CTA final ────────────────────────────────────── */}
            <section className="relative z-10 px-6 sm:px-10 pb-32 max-w-4xl mx-auto text-center" data-testid="solo-final-cta">
                <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4 }}
                >
                    <h2 className="text-3xl sm:text-5xl font-extrabold leading-tight mb-5">
                        Vos prochains clients{' '}
                        <span className="bg-gradient-to-r from-[#A855F7] to-[#FF914D] bg-clip-text text-transparent">
                            vous cherchent déjà.
                        </span>
                    </h2>
                    <p className="text-lg text-[#C7BEE6] mb-8 max-w-2xl mx-auto">
                        Créez votre compte en 30 secondes et commencez à collecter des avis dès aujourd&apos;hui.
                    </p>
                    <Link
                        to="/register/solo"
                        data-testid="solo-landing-final-cta"
                        className="group inline-flex items-center gap-3 px-8 py-5 rounded-full bg-gradient-to-r from-[#681793] to-[#FF914D] text-lg font-bold text-white shadow-2xl shadow-[#681793]/40 hover:opacity-95 transition-all hover:scale-[1.03]"
                    >
                        <Zap className="w-5 h-5" />
                        Créer mon compte solo
                        <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </Link>
                    <div className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-[#8A7DB8]">
                        <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5" /> Gratuit à essayer</span>
                        <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5" /> Sans engagement</span>
                        <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5" /> Vérification RSAC en 3 secondes</span>
                    </div>
                </motion.div>
            </section>

            {/* ── Footer minimal ───────────────────────────────── */}
            <footer className="relative z-10 px-6 sm:px-10 py-8 border-t border-white/5 text-center text-xs text-[#6B5A99] tracking-[2px]">
                <span className="font-bold bg-gradient-to-r from-[#A855F7] to-[#FF914D] bg-clip-text text-transparent">HOMLY.PRO</span>
                {' · '}RÉPUTATION VÉRIFIÉE POUR L&apos;IMMOBILIER
                {' · '}<Link to="/" className="hover:text-[#C7BEE6] transition-colors">Retour au site</Link>
            </footer>
        </div>
    );
}

// ── Sous-composants ──────────────────────────────────────────
const BenefitCard = ({ icon, title, text, accent }) => (
    <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-80px' }}
        transition={{ duration: 0.4 }}
        className="rounded-3xl border border-white/10 bg-black/40 backdrop-blur-md p-6 hover:border-[#A855F7]/40 transition-colors"
    >
        <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${accent} flex items-center justify-center mb-4 shadow-lg shadow-[#681793]/20`}>
            <span className="text-white">{icon}</span>
        </div>
        <h3 className="text-xl font-bold mb-2">{title}</h3>
        <p className="text-sm text-[#C7BEE6] leading-relaxed">{text}</p>
    </motion.div>
);

const StepCard = ({ number, title, text }) => (
    <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-80px' }}
        transition={{ duration: 0.4 }}
        className="relative rounded-3xl border border-white/10 bg-white/[0.02] p-6"
    >
        <div className="text-5xl font-extrabold bg-gradient-to-br from-[#A855F7] to-[#FF914D] bg-clip-text text-transparent mb-3 leading-none">
            {number}
        </div>
        <h3 className="text-lg font-bold mb-2">{title}</h3>
        <p className="text-sm text-[#C7BEE6] leading-relaxed">{text}</p>
    </motion.div>
);

const LimitItem = ({ children, ok }) => (
    <div className={`inline-flex items-center gap-2 ${ok ? 'text-[#8FE39B]' : 'text-[#FFA48D]'}`}>
        {ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5" />}
        <span className="text-white/85">{children}</span>
    </div>
);
